/**
 * DIEUKYDIEU.TV - TOPIC QUIZ MODAL MODULE
 * 2-step interactive quiz opened from Home carousel topic cards.
 *   Step 1: question + answer options ("Continue" enabled only after choosing).
 *   Step 2: recap of chosen answer + intro text + "Find the answer" CTA.
 *   CTA closes the modal and opens the mapped article (history: Home → Khám phá → Story).
 *
 * Built on native <dialog> + showModal(): focus trap, inert background and ESC come for free.
 * Light-dismiss uses `closedby="any"` with a click-on-backdrop fallback for older browsers.
 */

import { t } from './i18n.js';
import { state } from './state.js';

/**
 * Quiz content per topic, keyed by CAROUSEL_TOPICS[].id.
 * `articleId` maps to an article id in SITEMAP_DATA (mục Khám phá).
 * Each language block: q (question), options (3 answers), intro (step-2 lead-in).
 */
import { TOPIC_QUIZZES } from './data/topic-quiz-data.js';
export { TOPIC_QUIZZES };

const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E'];
const TOPIC_ID_MAP = { hue: 'love', danang: 'health', binhdinh: 'finance', vungtau: 'work', bentre: 'reincarnation' };

let quizState = {
    topic: null,      // CAROUSEL_TOPICS item
    quiz: null,       // TOPIC_QUIZZES entry
    selected: -1,     // selected option index
    step: 1
};
let eventsBound = false;

function resolveQuizId(topicId) {
    if (Object.prototype.hasOwnProperty.call(TOPIC_QUIZZES, topicId)) return topicId;
    return TOPIC_ID_MAP[topicId] || null;
}

export function hasTopicQuiz(topicId) {
    return !!resolveQuizId(topicId);
}

function getLocalized(quiz) {
    return quiz[state.currentLang] || quiz.vi;
}

function $(id) {
    return document.getElementById(id);
}

function escapeHtml(str) {
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/* --------------------------------------------------------------------------
   RENDERING
   -------------------------------------------------------------------------- */
function renderStaticLabels() {
    const closeBtn = $('tq-close-btn');
    if (closeBtn) {
        closeBtn.setAttribute('aria-label', t('quiz_close_aria'));
        closeBtn.setAttribute('title', t('quiz_close_aria'));
    }
    const setText = (id, key) => {
        const el = $(id);
        if (el) el.textContent = t(key);
    };
    setText('tq-continue-label', 'quiz_continue');
    setText('tq-find-label', 'quiz_find_answer');
    setText('tq-back-label', 'quiz_back');
    setText('tq-recap-label', 'quiz_your_choice');
    setText('tq-hint', 'quiz_hint');
}

function renderStep1() {
    const { topic, quiz, selected } = quizState;
    const content = getLocalized(quiz);

    const topicName = $('tq-topic-name');
    if (topicName) topicName.textContent = t(topic.key);

    const question = $('tq-question');
    if (question) question.textContent = content.q;

    const optionsWrap = $('tq-options');
    if (optionsWrap) {
        optionsWrap.innerHTML = content.options.map((opt, i) => `
            <label class="tq-option${i === selected ? ' is-selected' : ''}" style="--tq-i: ${i}">
                <input type="radio" name="tq-answer" value="${i}" ${i === selected ? 'checked' : ''}>
                <span class="tq-option-letter" aria-hidden="true">${OPTION_LETTERS[i] || i + 1}</span>
                <span class="tq-option-text">${escapeHtml(opt)}</span>
                <span class="tq-option-check" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </span>
            </label>
        `).join('');
    }

    syncContinueState();
}

function renderStep2() {
    const { quiz, selected } = quizState;
    const content = getLocalized(quiz);

    const recap = $('tq-recap-value');
    if (recap) recap.textContent = content.options[selected] || '';

    const recapLetter = $('tq-recap-letter');
    if (recapLetter) recapLetter.textContent = OPTION_LETTERS[selected] || '';

    const intro = $('tq-intro');
    if (intro) intro.textContent = content.intro;
}

function syncContinueState() {
    const btn = $('tq-continue-btn');
    const hint = $('tq-hint');
    const hasChoice = quizState.selected > -1;
    if (btn) btn.disabled = !hasChoice;
    if (hint) hint.classList.toggle('is-hidden', hasChoice);
}

function goToStep(step, direction = 'forward') {
    const dialog = $('topic-quiz-modal');
    const s1 = $('tq-step-1');
    const s2 = $('tq-step-2');
    if (!dialog || !s1 || !s2) return;

    quizState.step = step;
    dialog.setAttribute('data-step', String(step));

    const stepLabel = $('tq-step-label');
    if (stepLabel) stepLabel.textContent = t(step === 1 ? 'quiz_step_1' : 'quiz_step_2');

    const show = step === 1 ? s1 : s2;
    const hide = step === 1 ? s2 : s1;

    hide.hidden = true;
    hide.classList.remove('tq-anim-forward', 'tq-anim-back');
    show.hidden = false;
    show.classList.remove('tq-anim-forward', 'tq-anim-back');
    void show.offsetWidth; // restart animation
    show.classList.add(direction === 'back' ? 'tq-anim-back' : 'tq-anim-forward');

    // Move focus to a sensible element inside the new step
    requestAnimationFrame(() => {
        if (step === 1) {
            const checked = dialog.querySelector('input[name="tq-answer"]:checked');
            const target = (direction === 'back' && checked) ? checked : $('tq-question');
            if (target) target.focus({ preventScroll: true });
        } else {
            const findBtn = $('tq-find-btn');
            if (findBtn) findBtn.focus({ preventScroll: true });
        }
    });
}

/* --------------------------------------------------------------------------
   OPEN / CLOSE
   -------------------------------------------------------------------------- */
export function openTopicQuiz(topic) {
    const dialog = $('topic-quiz-modal');
    const quizKey = topic ? resolveQuizId(topic.id) : null;
    if (!dialog || !topic || !quizKey) return false;

    bindQuizEvents();

    quizState = {
        topic,
        quiz: TOPIC_QUIZZES[quizKey],
        selected: -1,
        step: 1
    };

    dialog.style.setProperty('--tq-rgb', topic.colorRGB || 'var(--accent-color-rgb)');
    renderStaticLabels();
    renderStep1();
    goToStep(1);

    if (!dialog.open) {
        document.documentElement.classList.add('tq-scroll-lock');
        if (typeof dialog.showModal === 'function') {
            dialog.showModal();
        } else {
            dialog.setAttribute('open', '');
        }
    }
    return true;
}

export function closeTopicQuiz() {
    const dialog = $('topic-quiz-modal');
    if (!dialog || !dialog.open) return;
    if (typeof dialog.close === 'function') {
        dialog.close();
    } else {
        dialog.removeAttribute('open');
        onDialogClosed();
    }
}

function onDialogClosed() {
    document.documentElement.classList.remove('tq-scroll-lock');
}

function findAnswer() {
    const { quiz } = quizState;
    if (!quiz) return;
    const articleId = quiz.articleId;

    closeTopicQuiz();

    // History: Home → Khám phá → Story, so Back from the article lands on Khám phá.
    if (typeof window.switchNav === 'function') {
        window.switchNav('category-view', 1);
    }
    if (typeof window.openStoryDetail === 'function') {
        window.openStoryDetail(articleId);
    }
}

/* --------------------------------------------------------------------------
   EVENTS
   -------------------------------------------------------------------------- */
function bindQuizEvents() {
    if (eventsBound) return;
    const dialog = $('topic-quiz-modal');
    if (!dialog) return;
    eventsBound = true;

    // Answer selection (native radios → keyboard arrows work out of the box)
    dialog.addEventListener('change', (e) => {
        const input = e.target;
        if (!input || input.name !== 'tq-answer') return;
        quizState.selected = parseInt(input.value, 10);
        dialog.querySelectorAll('.tq-option').forEach((label) => {
            const radio = label.querySelector('input');
            label.classList.toggle('is-selected', !!(radio && radio.checked));
        });
        syncContinueState();
    });

    // Enter on a chosen radio = Continue
    dialog.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && e.target && e.target.name === 'tq-answer' && quizState.selected > -1) {
            e.preventDefault();
            renderStep2();
            goToStep(2, 'forward');
        }
        // Keep global shortcuts (carousel arrows, player Space/M, search Esc) from firing behind the modal.
        // Native ESC → dialog "cancel" still works since it is not driven by these listeners.
        e.stopPropagation();
    });

    $('tq-continue-btn')?.addEventListener('click', () => {
        if (quizState.selected < 0) return;
        renderStep2();
        goToStep(2, 'forward');
    });

    $('tq-back-btn')?.addEventListener('click', () => {
        renderStep1();
        goToStep(1, 'back');
    });

    $('tq-find-btn')?.addEventListener('click', findAnswer);
    $('tq-close-btn')?.addEventListener('click', closeTopicQuiz);

    dialog.addEventListener('close', onDialogClosed);

    // Light-dismiss fallback for browsers without <dialog closedby> support
    if (!('closedBy' in HTMLDialogElement.prototype)) {
        dialog.addEventListener('click', (event) => {
            if (event.target !== dialog) return;
            const rect = dialog.getBoundingClientRect();
            const inside = rect.top <= event.clientY && event.clientY <= rect.top + rect.height &&
                rect.left <= event.clientX && event.clientX <= rect.left + rect.width;
            if (!inside) closeTopicQuiz();
        });
    }

    // Re-render text when the language changes while the modal is open
    window.addEventListener('dkd:langchange', () => {
        if (!dialog.open || !quizState.quiz) return;
        renderStaticLabels();
        renderStep1();
        if (quizState.step === 2) renderStep2();
        const stepLabel = $('tq-step-label');
        if (stepLabel) stepLabel.textContent = t(quizState.step === 1 ? 'quiz_step_1' : 'quiz_step_2');
    });
}
